import React from 'react';
import { Link } from 'react-router-dom';
import { ClinicStatus, ClinicStatusColor } from '../constants/ClinicStatus';
import { formatPhoneNumberForDisplay } from '../utils/phoneFormatter';

// Tooltip component for field descriptions
const Tooltip = ({ text }) => {
  return (
    <div className="group relative inline-block ml-1">
      <div className="cursor-help text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div style={{ textTransform: 'none' }} className="absolute z-10 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1">
        {text}
        <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 left-1/2 -translate-x-1/2 -bottom-1"></div>
      </div>
    </div>
  );
};

const ClinicList = ({ clinics }) => {
  if (!clinics || clinics.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No clinics found.</p>
      </div>
    );
  }

  // Field descriptions for tooltips
  const columnDescriptions = {
    name: "The name of the clinic as it appears to users and administrators.",
    id: "Unique identifier for the clinic. Used in API calls and database references.",
    region: "The geographical region where the clinic is located (e.g., Hong Kong, Singapore).",
    conciergeNumber: "The phone number used for the clinic's concierge service. This is the main contact point for patients.",
    wabaNumber: "WhatsApp Business API number used for sending messages to patients.",
    clinicNumber: "The WhatsApp number associated with the clinic for direct communication.",
    agentVersion: "The version of the concierge agent being used by this clinic (e.g., v1, v2, v3).",
    status: "The current operational status of the clinic. Can be Ready (configured but not live), Published (live and available), or Inactive (not available).",
    actions: "Available actions for this clinic, such as viewing details or conversations."
  };

  // Helper function to format phone number for wa.me link
  const formatPhoneForWhatsApp = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove +, spaces, and special characters
    return phoneNumber.replace(/[\s\-\(\)\+]/g, '');
  };

  // Helper function to get status display and styling
  const getStatusDisplay = (clinic) => {
    // Check if using the new status field
    if (clinic.status) {
      const status = clinic.status.toLowerCase();
      // Use the constants from ClinicStatus.jsx
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1), // Capitalize first letter
        className: ClinicStatusColor[status] || ClinicStatusColor.active // Default to active if not found
      };
    }
    // Fallback to legacy is_active field
    else {
      return {
        label: clinic.is_active ? 'Active' : 'Deactivated',
        className: clinic.is_active ? ClinicStatusColor.published : ClinicStatusColor.inactive
      };
    }
  };

  return (
    <table className="min-w-full border-collapse">
      <thead>
        <tr className="bg-gray-50">
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Name
              <Tooltip text={columnDescriptions.name} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              ID
              <Tooltip text={columnDescriptions.id} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Region
              <Tooltip text={columnDescriptions.region} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Concierge Number
              <Tooltip text={columnDescriptions.conciergeNumber} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              WABA Number
              <Tooltip text={columnDescriptions.wabaNumber} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Clinic Number
              <Tooltip text={columnDescriptions.clinicNumber} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Agent Version
              <Tooltip text={columnDescriptions.agentVersion} />
            </div>
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center">
              Status
              <Tooltip text={columnDescriptions.status} />
            </div>
          </th>
          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center justify-center">
              Actions
              <Tooltip text={columnDescriptions.actions} />
            </div>
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {clinics.map((clinic) => {
          const statusDisplay = getStatusDisplay(clinic);

          return (
            <tr
              key={clinic.id}
              className="hover:bg-gray-50"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                <Link to={`/clinics/${clinic.id}`} className="hover:underline">
                  {clinic.name}
                </Link>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {clinic.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {clinic.region === 'HK' ? 'Hong Kong (HK)' :
                  clinic.region === 'SG' ? 'Singapore (SG)' :
                    'Not specified'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <span>{formatPhoneNumberForDisplay(clinic.contact_number)}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  {formatPhoneNumberForDisplay(clinic.waba_number)}
                  {clinic.waba_number && (
                    <a
                      href={`https://wa.me/${formatPhoneForWhatsApp(clinic.waba_number)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-6 h-6 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                      title="Open in WhatsApp"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </a>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatPhoneNumberForDisplay(clinic.clinic_whatsapp_number)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {clinic.concierge_version || 'v1'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusDisplay.className}`}>
                  {statusDisplay.label}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex justify-center space-x-4">
                  <Link
                    to={`/clinics/${clinic.id}`}
                    className="w-10 h-10 flex items-center justify-center bg-[#075e54] text-white rounded-full hover:bg-[#054c44] transition-colors"
                    title="Clinic Info"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </Link>
                  <Link
                    to={`/clinics/${clinic.id}/conversations`}
                    className="w-10 h-10 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                    title="Conversations"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </Link>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ClinicList;
